/**
 * The mermaid configuration, as one object both the app and the verifier read.
 *
 * A diagram in a thread sits inches from the workflow editor's own graph, so the two should not
 * look like they came from different products. These values are `flowkit`'s — the node fill,
 * border and title from `flowkit.css`, the connector stroke from `flowEdge.js` — mapped onto
 * mermaid's `base` theme, which is the only built-in meant to be customised: it derives its
 * remaining ~100 variables from these roots, where `default`/`neutral`/`forest`/`dark` fight them.
 *
 * `secure` is set EXPLICITLY. Mermaid lets a diagram carry its own config in a `---` front-matter
 * block or a `%%{init}%%` directive, and `securityLevel: "strict"` does not stop it — strict governs
 * HTML in labels, while `secure` is the list of keys a directive may not touch. Its default does not
 * include `theme`, so without this an agent-authored artifact could render itself in `forest` inside
 * our pane. These definitions arrive over the network like any other artifact; appearance is ours.
 *
 * Kept in sync with `tools/mermaid/config.json` by `mermaidTheme.test.js` — the verifier renders
 * real diagrams through mermaid-cli, and a palette it did not share would verify nothing.
 */
export const MERMAID_CONFIG = {
  startOnLoad: false,
  securityLevel: 'strict',
  secure: ['secure', 'securityLevel', 'startOnLoad', 'maxTextSize', 'suppressErrorRendering',
           'theme', 'themeVariables', 'themeCSS', 'fontFamily'],
  theme: 'base',
  fontFamily: 'inherit',
  // ROOT `fontSize`, not `themeVariables.fontSize`, and not `sequence.actorFontSize`.
  //
  // A sequence diagram rendered a full size larger than everything beside it — 19 elements at 16px
  // against the flowchart's 13 — and the obvious knobs do nothing, silently. Its renderer assigns
  // the per-element sizes and then OVERWRITES all three from one value:
  //
  //     e.fontSize && (actorFontSize = noteFontSize = messageFontSize = e.fontSize)
  //
  // so `sequence.actorFontSize: 13` is applied and then thrown away. `themeVariables.fontSize`
  // never reaches it at all. Only this key works; measured every time, since none of it is
  // discoverable from the config types.
  fontSize: 13,
  // Geometry, tuned against the state diagram — the one that already read as compact. Mermaid's
  // defaults are sized for a full page; in a thread pane beside prose they left nodes floating in
  // padding with a rank of whitespace between them.
  flowchart: {
    // Also the only lever on a decision diamond, whose side mermaid computes as
    // `(labelWidth + padding) + (labelHeight + padding)` — a sum, so a wide label makes a TALL
    // diamond and no config reshapes it. Measured: 122 → 114 across this drop, against 126x36 →
    // 110x28 for a rectangle. Wrapping the label makes it worse, not better (135), because
    // wrapping trades width for height and the formula adds them.
    padding: 4,           // inside a node, was 15
    nodeSpacing: 30,      // between siblings, was 50
    rankSpacing: 36,      // between ranks — the gap that read as a gutter, was 50
    diagramPadding: 8
  },
  sequence: {
    width: 120,           // an actor box, was 150
    height: 44,           // was 65
    actorMargin: 30,      // between lifelines, was 50
    boxMargin: 8,         // was 10
    messageMargin: 26,    // between messages, was 35
    diagramMarginX: 16,   // was 50
    diagramMarginY: 10    // was 10
  },
  themeVariables: {
    darkMode: false,
    background: '#ffffff',
    fontFamily: 'inherit',
    fontSize: '13px',

    // A node: flowkit's flat square — light slate fill, a quiet border, near-black title.
    primaryColor: '#f2f4f6',
    primaryBorderColor: '#c8d1da',
    primaryTextColor: '#1f2937',
    mainBkg: '#f2f4f6',
    nodeBorder: '#c8d1da',
    textColor: '#1f2937',

    // The emphasised node — flowkit's selected state.
    secondaryColor: '#e8ebee',
    secondaryBorderColor: '#adb7c2',
    secondaryTextColor: '#1f2937',

    // The quiet surface — flowkit's tag chip.
    tertiaryColor: '#ffffff',
    tertiaryBorderColor: '#dfe3e8',
    tertiaryTextColor: '#5b6572',

    // Connectors: the outlined slate `flowEdge` draws, and its labels.
    lineColor: '#9aa3af',
    edgeLabelBackground: '#ffffff',
    arrowheadColor: '#9aa3af',

    // Notes and clusters.
    noteBkgColor: '#ffffff',
    noteBorderColor: '#dfe3e8',
    noteTextColor: '#5b6572',
    clusterBkg: '#fafbfc',
    clusterBorder: '#dfe3e8',
    titleColor: '#1f2937',

    // Sequence diagrams derive little from the roots, so their actors are set directly.
    actorBkg: '#f2f4f6',
    actorBorder: '#c8d1da',
    actorTextColor: '#1f2937',
    actorLineColor: '#c8d1da',
    signalColor: '#9aa3af',
    signalTextColor: '#1f2937',
    labelBoxBkgColor: '#f2f4f6',
    labelBoxBorderColor: '#c8d1da',
    labelTextColor: '#1f2937',
    loopTextColor: '#5b6572',
    activationBkgColor: '#e8ebee',
    activationBorderColor: '#adb7c2',
    sequenceNumberColor: '#ffffff',

    // Entity-relationship rows alternate, and both shades stay inside the palette.
    attributeBackgroundColorOdd: '#ffffff',
    attributeBackgroundColorEven: '#f7f8fa'
  }
};

export default MERMAID_CONFIG;
